import React from "react";
import { COLORS } from "../../../theme/colors";
import AgentAvatar from "./AgentAvatar";
import { useHoverTrigger } from "./TileHoverPreview";

// agent-experience-redesign-v1.md §4.2 — Single tile rendered in the
// Library iPhone-grid. Compact column-flex of:
//   • IconTile @ 96px (squircle + per-category gradient + glyph)
//   • Name (12px Outfit, 2-line max, centered)
//   • Status dot below (green / amber / gray / red)
//
// Density toggle controls overall scale: comfortable=96px,
// compact=64px. Click → routes to /agents/:id (handled by parent).
//
// D3 — useHoverTrigger fires onHover(rect) after 600ms hover (or
// 400ms long-press on touch); cancels on scroll. Parent renders
// the floating preview anchored to the rect.
// D8 — onContextMenu hands {x, y} to parent which renders kebab.

const STATUS_DOT_COLOR = {
  live:     COLORS.green,
  active:   COLORS.green,
  paused:   COLORS.orange,
  draft:    COLORS.textDim,
  archived: COLORS.textMuted,
};

export default function AgentTile({
  agent,
  density = "comfortable",  // "comfortable" | "compact"
  onClick,
  onContextMenu,
  onHover,
  onHoverEnd,
  active = false,
}) {
  const tileSize = density === "compact" ? 64 : 96;
  const dotSize = density === "compact" ? 6 : 8;
  const labelSize = density === "compact" ? 11 : 12;

  // Kill-switch override: tripped supervisor renders a red dot regardless
  // of nominal status. Falls back to status mapping otherwise.
  const tripped = !!(agent?.flow_config?.supervisor?.kill_switch_state?.tripped);
  const dotColor = tripped
    ? (COLORS.pink || "#ef4444")
    : (STATUS_DOT_COLOR[agent?.status] || COLORS.textDim);

  const hoverHandlers = useHoverTrigger({
    onTrigger: (rect) => onHover && onHover(agent, rect),
    onCancel: () => onHoverEnd && onHoverEnd(),
  });

  return (
    <button
      type="button"
      onClick={() => onClick && onClick(agent)}
      onContextMenu={(e) => {
        if (onContextMenu) {
          e.preventDefault();
          onContextMenu(agent, { x: e.clientX, y: e.clientY });
        }
      }}
      title={agent.name}
      {...hoverHandlers}
      style={{
        background: "transparent", border: "none", cursor: "pointer",
        padding: density === "compact" ? "10px 6px" : "12px 8px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: density === "compact" ? 6 : 8,
        borderRadius: 12,
        transition: "background 0.18s, transform 0.18s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `${COLORS.surface}55`;
        e.currentTarget.style.transform = "translateY(-1px)";
        // Fan to hover-trigger handler too (event delegation friendly).
        hoverHandlers.onMouseEnter && hoverHandlers.onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.transform = "translateY(0)";
        hoverHandlers.onMouseLeave && hoverHandlers.onMouseLeave(e);
      }}
    >
      <AgentAvatar
        agent={agent}
        size={tileSize}
        variant="tile"
      />
      <div style={{
        fontSize: labelSize, color: COLORS.text, fontWeight: 600,
        lineHeight: 1.25,
        textAlign: "center",
        maxWidth: tileSize + 16,
        // 2-line ellipsis (-webkit-line-clamp is the cross-browser path
        // even though it's prefixed; falls back to overflow:hidden).
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
      }}>{agent.name || "(unnamed)"}</div>
      <div style={{
        width: dotSize, height: dotSize, borderRadius: "50%",
        background: dotColor,
        boxShadow: tripped ? `0 0 6px ${dotColor}` : "none",
      }} />
    </button>
  );
}
